type GetRange1 = (end: number) => number[]
type GetRange2 = (start: number, end: number) => number[]
export const getRange: GetRange1 & GetRange2 = (
    num1: number,
    num2?: number,
): number[] => {
    const [start, end] = num2 === undefined ? [0, num1] : [num1, num2]
    const arr = []
    for (let i = start; i < end; i++) {
        arr.push(i)
    }
    return arr
}

export const inArray = <T>(haystack: T[], needle: T): boolean => {
    return haystack.some((e) => e === needle)
}

/**
 *
 * @param haystack an array of strings or numbers
 * @returns a function that holds a closure of the array and checks if the arg is in the array
 */
export const cachedInArray = <T extends string | number>(haystack: T[]) => {
    const list = haystack.reduce<DictionaryOf<string>>((dict, e) => {
        dict[e.toString()] = typeof e
        return dict
    }, {})

    return (key: string | number): key is T => {
        return list[key] !== undefined && list[key] === typeof key
    }
}
/**
 *
 * @param haystack an array of strings and/or numbers
 * @param keyGenerator a function that should take one haystack element as argument and return a string | number
 * @returns a function that holds a closure of the array and checks if the keyGenerator(arg) is in the array
 */
export const tranformIntoCachedInArray = <T>(
    haystack: T[],
    keyGenerator: (val: T) => string | number,
) => {
    const check = cachedInArray(haystack.map(keyGenerator))

    return (lookupItem: T): lookupItem is T => check(keyGenerator(lookupItem))
}

/**
 *
 * @param arr an array of elements
 * @param keyProvider a function that takes and element and returns a string
 * @returns an object that has as keys all the array elements modified by the keyProvider function and as values arrays with the coresponding element. The key-value pairs look like this: {keyProvider(e): [e]}
 */
export const groupArray = <T>(
    arr: T[],
    keyProvider: (e: T) => string,
): DictionaryOf<T[]> => {
    const result: DictionaryOf<T[]> = {}
    arr.forEach((e) => {
        const key = keyProvider(e)
        result[key] = result[key] || []
        result[key].push(e)
    })
    return result
}

export const withoutTheBools = <T>(arr: Array<T | boolean>): T[] =>
    arr.filter((e): e is T => typeof e !== 'boolean')
export const withoutTheUndefined = <T>(arr: Array<T | undefined>): T[] =>
    arr.filter((e): e is T => e !== undefined)
export const withNullsAsEmpty = <T>(arr: Array<T | null>): T[] => {
    const new_arr: T[] = new Array<T>(arr.length)
    arr.forEach((value, index) => {
        if (value) {
            new_arr[index] = value
        }
    })
    return new_arr
}

export const withoutFalsy = <T>(arr: Array<T | boolean | undefined>): T[] =>
    arr.filter((e): e is T => Boolean(e))

export const getElementIndex = <T>(
    list: T[],
    match: (e: T) => boolean,
): number => {
    for (let i = 0; i < list.length; i++) {
        if (match(list[i])) {
            return i
        }
    }

    return -1
}

/* Returns every element inbetween, including both ends
 * In case one or more of the ends are not in the array, an empty array is returned
 */
export const sliceByElement = <T>(arr: T[], element1: T, element2: T): T[] => {
    const index1 = getElementIndex(arr, (o) => o === element1)
    const index2 = getElementIndex(arr, (o) => o === element2)
    if (index1 === -1 || index2 === -1) {
        return []
    }
    return arr.slice(Math.min(index2, index1), Math.max(index2, index1) + 1)
}

// Returns an array of arrays, each with a maximum of chunkSize number of elements
export const chunks = <T>(arr: T[], chunkSize: number): T[][] => {
    return Array.from(new Array(Math.ceil(arr.length / chunkSize)), (_, i) =>
        arr.slice(i * chunkSize, i * chunkSize + chunkSize),
    )
}

// Returns an arry with only unique elements
export const uniqueArray = <T>(arr: T[]): T[] => {
    return Array.from(new Set(arr))
}
